/* RescheduleRequestsInbox — clinic-facing pending-request reviewer.
 *
 * Mounts on /doctor/appointments above the calendar/list. Renders
 * `null` when there are no pending requests so the page is unchanged
 * for the steady state. When there ARE pending requests, shows them
 * as a single attention-grabbing card with per-request approve /
 * decline actions.
 *
 * Approve flow:
 *   - Clinic clicks one of the patient's slot chips → confirms
 *   - PUT /api/doctor/reschedule-requests/{id}/approve { slot_index, note? }
 *   - Backend reuses the audit-safe reschedule path, so business-hours
 *     + conflict validation are the same as a doctor-direct reschedule.
 *   - Errors (slot conflict, outside hours) bubble up to the toast.
 *
 * Decline flow:
 *   - Clinic clicks Decline → small dialog asks for a note (required)
 *   - PUT /api/doctor/reschedule-requests/{id}/decline { note }
 *   - Patient sees the note on their appointment row + can resubmit.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    ArrowClockwise, ArrowsClockwise, CheckCircle, X, User, Calendar, Phone,
} from "@phosphor-icons/react";

const fmtSlot = (s) => {
    if (!s?.date || !s?.time) return "—";
    try {
        const d = new Date(`${s.date}T${s.time}:00`);
        const date = d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
        const [h, m] = s.time.split(":").map(Number);
        const period = h >= 12 ? "PM" : "AM";
        const h12 = ((h + 11) % 12) + 1;
        return `${date} · ${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
    } catch { return `${s.date} · ${s.time}`; }
};

const fmtRelative = (iso) => {
    if (!iso) return "";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
        return `${Math.floor(mins / 1440)}d ago`;
    } catch { return ""; }
};

export default function RescheduleRequestsInbox({ onResolved }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [declineTarget, setDeclineTarget] = useState(null);
    const [declineNote, setDeclineNote] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/doctor/reschedule-requests?status=pending");
            setRequests(data || []);
        } catch (err) {
            // Clinic-staff with no doctor_id binding will 403 — silently
            // hide the panel rather than show a scary error.
            if (err.response?.status !== 403) console.warn("inbox load failed", err);
            setRequests([]);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const approve = async (req, slotIndex) => {
        if (busyId) return;
        const slot = req.preferred_slots?.[slotIndex];
        if (!slot) return;
        if (!await confirmDialog({ title: `Approve reschedule to ${fmtSlot(slot)}? The original appointment will be marked rescheduled.`, confirmText: "Confirm", tone: "warn" })) return;
        setBusyId(req.id);
        try {
            await api.put(`/doctor/reschedule-requests/${req.id}/approve`, { slot_index: slotIndex });
            toast.success("Reschedule approved — patient notified");
            setRequests((arr) => arr.filter((r) => r.id !== req.id));
            onResolved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't approve — pick a different slot");
        } finally { setBusyId(null); }
    };

    const submitDecline = async () => {
        if (!declineTarget) return;
        if (!declineNote.trim()) {
            toast.error("Please add a short note so the patient knows what to try next");
            return;
        }
        setBusyId(declineTarget.id);
        try {
            await api.put(`/doctor/reschedule-requests/${declineTarget.id}/decline`, { note: declineNote.trim() });
            toast.success("Request declined — patient can submit new slots");
            setRequests((arr) => arr.filter((r) => r.id !== declineTarget.id));
            setDeclineTarget(null);
            setDeclineNote("");
            onResolved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't decline");
        } finally { setBusyId(null); }
    };

    if (loading || requests.length === 0) return null;

    return (
        <>
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="reschedule-inbox">
                <div className="px-5 py-3.5 border-b border-amber-200/70 bg-amber-100/40 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-amber-200 grid place-items-center shrink-0">
                            <ArrowsClockwise size={18} weight="fill" className="text-amber-700" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-700">PATIENT RESCHEDULE REQUESTS</div>
                            <h2 className="font-display text-base font-bold text-amber-900">
                                {requests.length} pending request{requests.length !== 1 ? "s" : ""} from your patients
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="divide-y divide-amber-200/60">
                    {requests.map((req) => (
                        <div key={req.id} className="p-5 space-y-3" data-testid={`request-${req.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-full bg-amber-100 grid place-items-center shrink-0">
                                        <User size={16} weight="duotone" className="text-amber-700" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-sm font-bold">{req.patient_name || "Patient"}</div>
                                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5">
                                            {req.patient_phone && (
                                                <span className="inline-flex items-center gap-1"><Phone size={11} />{req.patient_phone}</span>
                                            )}
                                            {req.appointment_snapshot && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={11} />
                                                    currently {req.appointment_snapshot.date} · {req.appointment_snapshot.time}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <span className="text-[10px] text-amber-700/80 font-semibold">{fmtRelative(req.created_at)}</span>
                            </div>

                            {req.reason && (
                                <div className="text-xs text-amber-900/90 bg-amber-100/30 rounded-md px-2.5 py-2 border border-amber-200/40 italic">
                                    "{req.reason}"
                                </div>
                            )}

                            <div className="space-y-1.5" data-testid={`request-slots-${req.id}`}>
                                <div className="text-[10px] font-bold tracking-wider uppercase text-amber-700">
                                    Patient's preferred slots — click to approve
                                </div>
                                {(req.preferred_slots || []).map((s, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => approve(req, idx)}
                                        disabled={busyId === req.id}
                                        className="w-full text-left rounded-lg border border-amber-200 bg-white px-3 py-2.5 hover:border-emerald-400 hover:bg-emerald-50/40 transition-all flex items-center justify-between gap-2 group disabled:opacity-50"
                                        data-testid={`request-${req.id}-slot-${idx}`}
                                    >
                                        <div>
                                            <span className="text-[10px] font-bold text-amber-700/80 tabular-nums mr-2">#{idx + 1}</span>
                                            <span className="text-sm font-semibold">{fmtSlot(s)}</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground group-hover:text-emerald-700 font-semibold inline-flex items-center gap-1">
                                            <CheckCircle size={12} weight="fill" /> Approve this slot
                                        </span>
                                    </button>
                                ))}
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeclineTarget(req)}
                                    disabled={busyId === req.id}
                                    className="text-rose-700 hover:bg-rose-50 hover:text-rose-800 rounded-full text-xs"
                                    data-testid={`request-${req.id}-decline`}
                                >
                                    <X size={12} className="mr-1" /> None of these work — decline
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Dialog open={!!declineTarget} onOpenChange={(o) => !o && !busyId && setDeclineTarget(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-lg font-bold">Decline reschedule request</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Add a quick note so the patient knows what to try next.
                    </p>
                    <Textarea
                        rows={3}
                        value={declineNote}
                        onChange={(e) => setDeclineNote(e.target.value)}
                        placeholder="e.g. None of those slots have a chair free — please try mornings (9-11am) instead"
                        className="mt-2 text-sm"
                        data-testid="decline-note-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeclineTarget(null)} disabled={!!busyId} className="rounded-full">Cancel</Button>
                        <Button
                            onClick={submitDecline}
                            disabled={!!busyId || !declineNote.trim()}
                            className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
                            data-testid="decline-submit-btn"
                        >
                            {busyId ? "Declining…" : "Send decline"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
